import {isEqual} from 'lodash-es'
import {useCallback, useMemo, useState} from 'react'

import {usePreviousValue} from '../../hooks/common/use-previous-value'
import {useViews} from '../../hooks/use-views'
import {usePaginatedMemexItemsQueryVariables} from '../../state-providers/memex-items/queries/use-paginated-memex-items-query-variables'
import {ExpandedRowsContext} from './use-expanded-rows'

export function ExpandedRowsProvider({children}: {children?: React.ReactNode}) {
  // Map of (view number) => (array of expanded row IDs)
  const [rowMap, setRowMap] = useState<Record<number, Array<string>>>({})
  const {currentView} = useViews()
  const currentViewNumber = currentView?.number
  const variables = usePaginatedMemexItemsQueryVariables()
  const prevSortedBy = usePreviousValue(variables.sortedBy)

  // Clear expanded rows when sortedBy changes
  const sortedByChanged = !isEqual(variables.sortedBy, prevSortedBy)

  if (sortedByChanged) {
    if (currentViewNumber && (rowMap[currentViewNumber]?.length ?? 0) > 0) {
      // Clear expanded rows for the current view
      setRowMap({
        ...rowMap,
        [currentViewNumber]: [],
      })
    }
  }

  const toggleRowExpanded = useCallback(
    (rowId: string) => {
      if (!currentViewNumber) return
      setRowMap(prevMap => {
        const prevExpandedRows = prevMap[currentViewNumber] || []
        if (prevExpandedRows.includes(rowId)) {
          return {
            ...prevMap,
            [currentViewNumber]: prevExpandedRows.filter(id => id !== rowId),
          }
        }
        return {
          ...prevMap,
          [currentViewNumber]: [...prevExpandedRows, rowId],
        }
      })
    },
    [currentViewNumber],
  )

  const getExpandedRowsForView = useCallback(
    (viewNumber: number) => {
      return rowMap[viewNumber] || []
    },
    [rowMap],
  )

  const expandedRows = useMemo(() => {
    if (!currentViewNumber) return []
    return getExpandedRowsForView(currentViewNumber)
  }, [currentViewNumber, getExpandedRowsForView])

  const isRowExpandedForView = useCallback(
    (viewNumber: number, rowId: string) => {
      const expandedRowsForView = getExpandedRowsForView(viewNumber)
      return expandedRowsForView.includes(rowId)
    },
    [getExpandedRowsForView],
  )

  const isRowExpanded = useCallback(
    (rowId: string) => {
      if (!currentView) return false
      return isRowExpandedForView(currentView.number, rowId)
    },
    [currentView, isRowExpandedForView],
  )

  return (
    <ExpandedRowsContext.Provider
      value={useMemo(
        () => ({
          expandedRows,
          toggleRowExpanded,
          getExpandedRowsForView,
          isRowExpanded,
          isRowExpandedForView,
        }),
        [expandedRows, getExpandedRowsForView, isRowExpanded, isRowExpandedForView, toggleRowExpanded],
      )}
    >
      {children}
    </ExpandedRowsContext.Provider>
  )
}

try{ ExpandedRowsProvider.displayName ||= 'ExpandedRowsProvider' } catch {}